import React, { Component, Fragment } from 'react';

import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn
} from 'material-ui/Table';

import CircularProgress from 'material-ui/CircularProgress';
import HistoryIcon from 'material-ui/svg-icons/action/history';
import AvSortByAlphaIcon from 'material-ui/svg-icons/av/sort-by-alpha';
import ContentSortIcon from 'material-ui/svg-icons/content/sort';
import IconButton from 'material-ui/IconButton';
import { TableCell } from '@material-ui/core';
import IncidentHistoryTimelinePager from './IncidentHistoryTimelinePager';
import theme from '../../../muiTheme';
import { formatAttribute, getFieldType } from '../../../utils/helpers';

import IncrementalUpdateTime from './IncrementalUpdateTime';

import DownloadAsCsvButton from '../../common/DownloadAsCsvButton';

import { fieldGroupings } from '../../../defaults/fieldGroupings.json';

const styles = {
  tableHeaderToolsWrapperLegend: {
    alignItems: 'left'
  },
  tableHeaderToolsWrapper: {
    display: 'flex',
    flexDirection: 'row-reverse'
  },
  tableWrapper: {
    display: 'flex',
    flexDirection: 'row-reverse',
    borderTop: '1px solid #d8dde6'
  },
  fixedTable: {
    flex: '0 0 400px'
  },
  logicalGroupRow: {
    background: '#efefef',
    borderBottom: '3px solid #ccc'
  },
  fixedLogicalGroupCol: {
    padding: '8px 12px',
    fontSize: '1.1rem',
    borderRight: '3px solid #ccc',
    fontWeight: 600
  },
  logicalGroupCol: {
    padding: '8px 12px'
  },
  lastModifiedByCol: {
    width: '100px',
    paddingLeft: '12px'
  },
  DataElementsCol: {
    width: '200px',
    fontWeight: '600',
    borderRight: '3px solid #ccc'
  },
  table: {
    overflowX: 'auto',
    flex: '1 0 200px'
  },
  tableBody: {
    overflowX: 'visible',
    overflow: 'initial'
  },
  fieldCol: {
    width: '200px',
    overflow: 'visible',
    whiteSpace: 'normal',
    textOverflow: 'initial'
  },
  headerCol: {
    height: '50px',
    fontWeight: '600'
  },
  tableHeaderUpdateTime: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  updateTimeContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  timeIcon: {
    margin: '0 3px',
    color: 'currentColor',
    height: '18px',
    width: '18px'
  }
};

class IncidentHistoryTimeline extends Component {
  constructor(props) {
    super(props);

    this.state = {
      page: 0,
      pages: 10,
      pageSize: 5,
      sortByLogicalGrouping: true
    };
  }

  setPageSize = () => {
    const tableWidth = document.getElementById('historyTimelineTable')
      .clientWidth;
    const pageSize = Math.floor(tableWidth / 200) - 1;

    this.setState({
      pageSize
    });
  };

  loadInitialItems() {
    this.props.incidentsAreLoading();
  }

  getPageOfIncidents = () => {
    const allIncidents = this.props.incidents.sort(this.sortByModifiedDate);
    const start = this.state.page * this.state.pageSize;
    const end = start + this.state.pageSize;
    return allIncidents.slice(start, end);
  };

  getDataObjects(items) {
    return items.map(incident => ({ ...incident.attributes }));
  }

  renderLoadingIndicator() {
    return (
      <div style={{ textAlign: 'center' }}>
        <CircularProgress size={120} />
        <p style={{ color: theme.palette.textColor }}>Loading Timeline</p>
      </div>
    );
  }

  renderFixedTableHead() {
    return (
      <TableRow>
        <TableHeaderColumn
          style={{ ...styles.headerCol, ...styles.lastModifiedByCol }}
        >
          Last Modified By
        </TableHeaderColumn>
        <TableHeaderColumn
          style={{ ...styles.headerCol, ...styles.DataElementsCol }}
        >
          Data Elements
        </TableHeaderColumn>
      </TableRow>
    );
  }

  sortByModifiedDate(a, b) {
    return b.attributes.ModifiedOnDateTime - a.attributes.ModifiedOnDateTime;
  }

  renderTableHead() {
    const incidents = this.getPageOfIncidents();

    // These variables are needed to keep IncrementalUpdateTime on paginated incidents
    const allIncidents = this.props.incidents;
    const { page, pageSize } = this.state;

    const style = {
      fontWeight: 'bold'
    };

    return (
      <TableRow style={style}>
        {incidents.map((incident, i) => {
          const indexOffset = i + 1 + page * pageSize;

          return (
            <TableHeaderColumn
              style={{ ...styles.headerCol, ...styles.fieldCol }}
              key={incident.attributes.IrwinID}
            >
              <div>{incident.attributes.ModifiedBySystem}</div>
              <div style={styles.tableHeaderUpdateTime}>
                <IncrementalUpdateTime
                  datetime={incident.attributes.ModifiedOnDateTime}
                  from="now"
                />
                {// only render this second IncrementalUpdateTime if there is a previous incident
                indexOffset < allIncidents.length && (
                  <div style={styles.updateTimeContainer}>
                    <IncrementalUpdateTime
                      datetime={
                        allIncidents[indexOffset].attributes.ModifiedOnDateTime
                      }
                      from={incident.attributes.ModifiedOnDateTime}
                    />
                    <HistoryIcon style={styles.timeIcon} />
                  </div>
                )}
              </div>
            </TableHeaderColumn>
          );
        })}
      </TableRow>
    );
  }

  renderFixedTableBody = () => {
    const fields = this.props.fieldDefinitions;

    let tableRows;

    if (this.state.sortByLogicalGrouping) {
      tableRows = [];

      // copy to also include the ModifiedOnDateTime field as the very first row
      const modifiedOnField = fields.find(f => f.name === 'ModifiedOnDateTime');

      tableRows.push(
        <TableRow key={`${modifiedOnField.name}_first`}>
          <TableRowColumn
            style={{ ...styles.lastModifiedByCol, height: 'auto' }}
          >
            {this.getLastModifiedBy(modifiedOnField.name)}
          </TableRowColumn>
          <TableRowColumn style={{ ...styles.DataElementsCol, height: 'auto' }}>
            {modifiedOnField.alias}
          </TableRowColumn>
        </TableRow>
      );

      // create TableRows for each "logical group"
      // followed by rows for each field in that group
      fieldGroupings.forEach(groupInfo => {
        tableRows.push(
          <TableRow
            key={groupInfo.groupAlias}
            style={{ ...styles.logicalGroupRow }}
          >
            <TableRowColumn
              colSpan="2"
              style={{ ...styles.fixedLogicalGroupCol }}
            >
              {groupInfo.groupAlias}
            </TableRowColumn>
          </TableRow>
        );

        groupInfo.fieldNames.forEach(fieldNameInGroup => {
          const field = fields.find(f => f.name === fieldNameInGroup);

          // TODO workaround for fields that don't exist across environments
          // skip fields that aren't in dataset
          if (!field) {
            return;
          }
          // END workaround

          tableRows.push(
            <TableRow key={field.name}>
              <TableRowColumn
                style={{ ...styles.lastModifiedByCol, height: 'auto' }}
              >
                {this.getLastModifiedBy(field.name)}
              </TableRowColumn>
              <TableRowColumn
                style={{ ...styles.DataElementsCol, height: 'auto' }}
              >
                {field.alias}
              </TableRowColumn>
            </TableRow>
          );
        });
      });
    } else {
      // simple a-z sorting with ModifiedOnDateTime field at the top
      // (provided by parent IncidentHistory.js)
      tableRows = fields.map(field => (
        <TableRow key={field.name}>
          <TableRowColumn
            style={{ ...styles.lastModifiedByCol, height: 'auto' }}
          >
            {this.getLastModifiedBy(field.name)}
          </TableRowColumn>
          <TableRowColumn style={{ ...styles.DataElementsCol, height: 'auto' }}>
            {field.alias}
          </TableRowColumn>
        </TableRow>
      ));
    }

    return tableRows;
  };

  renderTableBody() {
    const fieldNames = this.props.fieldDefinitions.map(def => def.name);
    const incidents = this.getPageOfIncidents();

    let tableRows;

    if (this.state.sortByLogicalGrouping) {
      tableRows = [];

      // copy to also include the ModifiedOnDateTime field as the very first row
      const modifiedOnFieldName = fieldNames.find(
        f => f === 'ModifiedOnDateTime'
      );

      tableRows.push(
        <TableRow key={`${modifiedOnFieldName}_first`}>
          {this.renderTableCells(incidents, modifiedOnFieldName)}
        </TableRow>
      );

      // create TableRows for each "logical group"
      // followed by rows for each field in that group
      fieldGroupings.forEach(groupInfo => {
        tableRows.push(
          <TableRow key={groupInfo.groupAlias} style={styles.logicalGroupRow}>
            <TableRowColumn
              colSpan={incidents.length}
              style={styles.logicalGroupCol}
            />
          </TableRow>
        );

        groupInfo.fieldNames.forEach(fieldNameInGroup => {
          const fieldName = fieldNames.find(f => f === fieldNameInGroup);

          // TODO workaround for fields that don't exist across environments
          // skip fields that aren't in dataset
          // remove if check when we migrate to 1.5.0 in test/oat/prod
          if (fieldName) {
            tableRows.push(
              <TableRow key={fieldName}>
                {this.renderTableCells(incidents, fieldName)}
              </TableRow>
            );
          }
        });
      });
    } else {
      // simple a-z sorting with ModifiedOnDateTime field at the top
      // (provided by parent IncidentHistory.js)
      tableRows = fieldNames.map(fieldName => (
        <TableRow key={fieldName}>
          {this.renderTableCells(incidents, fieldName)}
        </TableRow>
      ));
    }

    return tableRows;
  }

  getCellStyle(isUpdatedField, incident, field, i) {
    const updatedFields = this.state.updatedFields;
    const incidentsItem = this.props.incidents.length - 1;
    // if lastModifiedBy === IRWIN (yellow)
    // if field is derived (green)
    // any other changes (blue)
    // everything else (white)
    if (isUpdatedField) {
      if (incident.attributes.ModifiedBySystem === 'irwin') {
        return {
          background: 'rgba(198, 192, 62, 0.5)'
        };
      }
      if (
        field === 'IncidentTypeCategory' &&
        incident.attributes.IncidentTypeCategory === 'OR' &&
        this.props.incident.derived.hasOR === true &&
        incident.attributes.IsQuarantined === 1 &&
        incident.attributes.IsValid === 1
      ) {
        return {
          background: 'rgba(14, 143, 73, .3)'
        };
      }
      if (
        (updatedFields[i].includes('POOLongitude') ||
          updatedFields[i].includes('POOLatitude')) &&
          (field === 'GACC' || field === 'POOJurisdictionalUnit' || field === 'POODispatchCenterID' || field === 'POOJurisdictionalAgency' ||
          field === 'POOJurisdictionalUnit' ||  field === 'POOProtectingAgency' || field === 'POOProtectingAgency' || field === 'POOCounty' ||
            field === 'POOFips' || field === 'POOState'|| field === 'POOLandownerCategory'
            || field === 'POOLandownerKind'))
      {
        return {
          background: 'rgba(14, 143, 73, .3)'
        };
      }
      return {
        background: 'rgba(80,167,221, .3)'
      };
    }
    if (
        (field === 'GACC' || field === 'POOJurisdictionalUnit' || field === 'POODispatchCenterID'
            ||  field === 'POOProtectingAgency' || field === 'POOProtectingAgency' || field === 'POOCounty' ||
            field === 'POOFips' || field === 'POOState'|| field === 'POOLandownerCategory'
            || field === 'POOLandownerKind')  && (this.props.incidents[incidentsItem].attributes === incident.attributes)
    ) {
      return {
        background: 'rgba(14, 143, 73, .3)'
      };
    }
  }

  formatCellValue(field, value) {
    return formatAttribute(field, value, 'incidentHistory').value;
  }

  renderTableCells(incidents, field) {
    return incidents.map((incident, i) => {
      const { page, pageSize } = this.state;

      const indexOffset = i + page * pageSize;

      const isUpdatedField = this.checkIfUpdatedField(field, indexOffset);
      const formattedValue = this.formatCellValue(
        field,
        incident.attributes[field]
      );

      const tooltipText =
        typeof formattedValue === 'object'
          ? formattedValue.props.children
          : formattedValue;

      return (
        <TableRowColumn
          key={incident.attributes.IrwinID}
          title={tooltipText}
          style={{
            ...styles.fieldCol,
            ...this.getCellStyle(isUpdatedField, incident, field, indexOffset)
          }}
        >
          {formattedValue}
        </TableRowColumn>
      );
    });
  }

  checkIfUpdatedField(field, i) {
    const updatedFields = this.state.updatedFields;

    if (!updatedFields[i]) return false;
    if (
      updatedFields[i].includes('POOLongitude') ||
      updatedFields[i].includes('POOLatitude')
    ) {
      // updatedFields[i].push('POOLandownerCategory', 'POOLandownerKind', 'GACC','POOJurisdictionalUnit',
      //     'POODispatchCenterID', 'POOProtectingAgency', 'POOProtectingAgency', 'POOCounty', 'POOFips', 'POOState',
      //     'POOLandownerCategory', 'POOLandownerKind' );
    }
    return updatedFields[i].includes(field);
  }

  getLastModifiedBy(field) {
    if (!this.props.incidents.length) return;

    if (this.props.incidents.length === 1) {
      return this.props.incidents[0].attributes.ModifiedBySystem;
    }

    const updatedFields = this.state.updatedFields;

    let latestModificationIndex = this.props.incidents.length - 1;

    Object.keys(updatedFields).forEach(i => {
      if (updatedFields[i].includes(field)) {
        latestModificationIndex =
          latestModificationIndex > i ? i : latestModificationIndex;
      }
    });

    return this.props.incidents[latestModificationIndex].attributes
      .ModifiedBySystem;
  }

  initialize(items) {
    if (!items.length) {
      this.loadInitialItems();
    }

    this.setState({ initialized: true });
  }

  setTableHeight = () => {
    const fixedTable = document.getElementById('historyTimelineFixedTable');
    const table = document.getElementById('historyTimelineTable');

    // sometimes these elements are not found
    if (!fixedTable && !table) {
      return;
    }

    for (let i = 0; i < fixedTable.children.length; i++) {
      // sometimes "table" does not have children
      if (!table.children[i]) {
        return;
      }

      const fixedFieldTableRow = fixedTable.children[i];
      const rowHeight = table.children[i].offsetHeight;
      fixedFieldTableRow.style.height = `${rowHeight}px`;
    }
  };

  componentDidMount() {
    // add event listener to update page size on window resize
    window.addEventListener('resize', this.setPageSize);
    this.setPageSize();

    if (!this.props.loading) {
      this.setTableHeight();
    }

    if (this.props.static) return;

    if (this.props.ready) {
      this.initialize(this.props.items, this.props.pollInterval);
    }
  }

  componentDidUpdate() {
    if (!this.props.loading) {
      this.setTableHeight();
    }
  }

  componentWillUnmount() {
    // remove event listener to update page size on window resize
    window.removeEventListener('resize', this.setPageSize);
  }

  static getDerivedStateFromProps(props, state) {
    if (!props.incidents) return null;

    const incidents = props.incidents.sort(
      (a, b) =>
        b.attributes.ModifiedOnDateTime - a.attributes.ModifiedOnDateTime
    );

    const findUpdatedFields = incidents => {
      const diffs = [];

      if (incidents.length) {
        incidents.forEach((currentIncident, i) => {
          if (i + 1 === incidents.length) return;
          const previousIncident = incidents[i + 1];

          const changes = Object.keys(currentIncident.attributes).reduce(
            (diff, key) => {
              if (
                currentIncident.attributes[key] ===
                previousIncident.attributes[key]
              )
                return diff;

              return [...diff, key];
            },
            []
          );

          diffs.push(changes);
        });
      }
      return diffs;
    };

    return {
      updatedFields: findUpdatedFields(incidents),
      pages: incidents.length / state.pageSize
    };
  }

  handlePageChanged = page => {
    this.setState({ page });
  };

  toggleSortByLogicalGrouping = () => {
    this.setState({ sortByLogicalGrouping: !this.state.sortByLogicalGrouping });
  };

  render() {
    if (this.props.loading) {
      return this.renderLoadingIndicator();
    }

    const { fieldDefinitions, getCsvData } = this.props;
    const incidentName = this.props.incident.attributes.IncidentName;
    const { page, pages, sortByLogicalGrouping } = this.state;

    return (
      <div>
        <div style={styles.tableHeaderToolsWrapper}>
          <IconButton
            onClick={this.toggleSortByLogicalGrouping}
            tooltip={
              sortByLogicalGrouping
                ? 'Sort alphabetically'
                : 'Show logical groups'
            }
            tooltipPosition="bottom-left"
          >
            {sortByLogicalGrouping ? (
              <AvSortByAlphaIcon />
            ) : (
              <ContentSortIcon />
            )}
          </IconButton>
          <DownloadAsCsvButton
            tooltip="Download Incident History as CSV"
            fields={fieldDefinitions}
            exportedFilename={`${incidentName}_history.csv`}
            dataPromise={getCsvData}
          />
          <IncidentHistoryTimelinePager
            page={page}
            pages={pages}
            onChange={this.handlePageChanged}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row-reverse',
              margin: 'auto',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              alignItems: 'flex-end'
            }}
          >
            <TableRowColumn
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'rgba(14, 143, 73, .3)'
              }}
            >
              IRWIN Derived
            </TableRowColumn>
            <TableRowColumn
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'rgba(198, 192, 62, 0.5)'
              }}
            >
              IRWIN Director Modified
            </TableRowColumn>
            <TableRowColumn
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'rgba(80,167,221, .3)'
              }}
            >
              Participating System Modified
            </TableRowColumn>
            <TableRowColumn
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              Legend:
            </TableRowColumn>
          </div>
        </div>

        <div style={styles.tableWrapper}>
          <Table
            wrapperStyle={styles.table}
            bodyStyle={styles.tableBody}
            selectable={false}
          >
            <TableHeader
              displaySelectAll={false}
              adjustForCheckbox={false}
              enableSelectAll={false}
            >
              {this.renderTableHead()}
            </TableHeader>
            <TableBody
              id="historyTimelineTable"
              displayRowCheckbox={false}
              showRowHover={false}
            >
              {this.renderTableBody()}
            </TableBody>
          </Table>

          <Table wrapperStyle={styles.fixedTable} selectable={false}>
            <TableHeader
              displaySelectAll={false}
              adjustForCheckbox={false}
              enableSelectAll={false}
            >
              {this.renderFixedTableHead()}
            </TableHeader>
            <TableBody
              id="historyTimelineFixedTable"
              displayRowCheckbox={false}
              showRowHover={false}
            >
              {this.renderFixedTableBody()}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }
}

export default IncidentHistoryTimeline;



// WEBPACK FOOTER //
// ./src/components/incidents/IncidentDetails/IncidentHistoryTimeline.js